/**
 * ReservationTable - 테이블(컬럼) 방식 예약 리스트
 * Phase 0D2: AppointmentTimeline 직접 사용 (Consult 경유 제거)
 * 컬럼: 신청일자, 예약자명, 연락처, 진료과목, 예약 일시, AI요약, 상태, 액션
 */

import dayjs from "dayjs";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ReservationQuickActions } from "./ReservationQuickActions";
import type { AppointmentTimeline } from "@/types/appointment";
import { cn } from "@/lib/utils";
import { AppointmentStatusBadge } from "@/components/common/StatusBadge";

interface ReservationTableProps {
  appointments: AppointmentTimeline[];
  isLoading: boolean;
  onAppointmentClick: (appointment: AppointmentTimeline) => void;
  onPrioritySelect?: (data: AppointmentTimeline) => void;
}

// BL-131: 직관적 날짜 포맷 (예: "2월 21일 (금) 14:30")
const DAY_NAMES = ["일", "월", "화", "수", "목", "금", "토"];

function formatIntuitiveDatetime(date?: string | null, time?: string | null): string | null {
  if (!date) return null;
  const d = dayjs(date);
  if (!d.isValid()) return null;
  const month = d.month() + 1;
  const day = d.date();
  const dayName = DAY_NAMES[d.day()];
  const datePart = `${month}월 ${day}일 (${dayName})`;
  return time ? `${datePart} ${time}` : datePart;
}

function formatChoiceDateTime(date?: string | null, time?: string | null) {
  if (!date) return null;
  return formatIntuitiveDatetime(date, time);
}

export function ReservationTable({
  appointments,
  isLoading,
  onAppointmentClick,
  onPrioritySelect,
}: ReservationTableProps) {
  if (isLoading) {
    return (
      <Card>
        <div className="p-8 text-center text-muted-foreground">
          <div className="flex justify-center mb-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
          로딩 중...
        </div>
      </Card>
    );
  }

  if (appointments.length === 0) {
    return (
      <Card>
        <div className="p-12 text-center text-muted-foreground">
          예약 내역이 없습니다.
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left px-3 py-2.5 text-xs font-medium text-muted-foreground whitespace-nowrap">
                신청일자
              </th>
              <th className="text-left px-3 py-2.5 text-xs font-medium text-muted-foreground whitespace-nowrap">
                예약자명
              </th>
              <th className="text-left px-3 py-2.5 text-xs font-medium text-muted-foreground whitespace-nowrap">
                연락처
              </th>
              <th className="text-left px-3 py-2.5 text-xs font-medium text-muted-foreground whitespace-nowrap">
                진료과목
              </th>
              <th className="text-left px-3 py-2.5 text-xs font-medium text-muted-foreground whitespace-nowrap">
                예약 일시
              </th>
              <th className="text-left px-3 py-2.5 text-xs font-medium text-muted-foreground whitespace-nowrap">
                AI요약
              </th>
              <th className="text-center px-3 py-2.5 text-xs font-medium text-muted-foreground whitespace-nowrap">
                상태
              </th>
              <th className="text-center px-3 py-2.5 text-xs font-medium text-muted-foreground whitespace-nowrap">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {appointments.map((appointment) => {
              const first = formatChoiceDateTime(
                appointment.firstChoiceDate,
                appointment.firstChoiceTime,
              );
              const second = formatChoiceDateTime(
                appointment.secondChoiceDate,
                appointment.secondChoiceTime,
              );
              const isRequested =
                appointment.status === "REQUESTED" ||
                appointment.status === "CHECKING";

              return (
                <tr
                  key={appointment.id}
                  className={cn(
                    "hover:bg-accent/50 transition-colors cursor-pointer",
                    isRequested && "bg-warning/5",
                  )}
                  onClick={() => onAppointmentClick(appointment)}
                >
                  {/* 신청일자 — BL-131: 직관적 포맷 */}
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs text-muted-foreground">
                    {appointment.occurredAt
                      ? (() => {
                          const d = dayjs(appointment.occurredAt);
                          return `${d.month() + 1}/${d.date()} (${DAY_NAMES[d.day()]}) ${d.format("HH:mm")}`;
                        })()
                      : "-"}
                  </td>

                  {/* 예약자명 */}
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-sm">
                        {appointment.customerName || "-"}
                      </span>
                      {appointment.isVip && (
                        <Badge
                          variant="secondary"
                          className="text-[9px] h-4 px-1 bg-warning/15 text-warning"
                        >
                          VIP
                        </Badge>
                      )}
                      {appointment.isThirdParty && (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="secondary"
                                className="text-[9px] h-4 px-1 bg-violet-100 text-violet-800 cursor-help"
                              >
                                대리
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              <p>
                                예약자: {appointment.bookerName || "정보 없음"}
                                {appointment.relationship &&
                                  appointment.relationship !== "SELF" &&
                                  ` (${
                                    ({
                                      PARENT: "부모",
                                      CHILD: "자녀",
                                      SPOUSE: "배우자",
                                      SIBLING: "형제자매",
                                      GUARDIAN: "보호자",
                                      OTHER: "기타",
                                    } as Record<string, string>)[
                                      appointment.relationship
                                    ] || appointment.relationship
                                  })`}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {appointment.requiresReview && (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="secondary"
                                className="text-[9px] h-4 px-1 bg-amber-100 text-amber-800 cursor-help"
                              >
                                확인필요
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              <p>{appointment.reviewReason || "환자 정보 확인이 필요합니다"}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {appointment.priority === "URGENT" && (
                        <Badge
                          variant="destructive"
                          className="text-[9px] h-4 px-1"
                        >
                          긴급
                        </Badge>
                      )}
                    </div>
                  </td>

                  {/* 연락처 */}
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs text-muted-foreground">
                    {appointment.callerNumber || appointment.customerPhone || "-"}
                  </td>

                  {/* 진료과목 */}
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                    {appointment.serviceName || appointment.department || "-"}
                  </td>

                  {/* 예약 일시 */}
                  <td className="px-3 py-2.5 text-xs">
                    <div className="space-y-0.5">
                      {first ? (
                        <>
                          <div className="whitespace-nowrap">
                            <span className="text-muted-foreground">1순위</span>{" "}
                            {first}
                          </div>
                          {second && (
                            <div className="whitespace-nowrap text-muted-foreground">
                              <span>2순위</span> {second}
                            </div>
                          )}
                        </>
                      ) : appointment.appointmentDate ? (
                        <span className="whitespace-nowrap">
                          {formatIntuitiveDatetime(
                            dayjs(appointment.appointmentDate).format("YYYY-MM-DD"),
                            dayjs(appointment.appointmentDate).format("HH:mm") !== "00:00"
                              ? dayjs(appointment.appointmentDate).format("HH:mm")
                              : null,
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </div>
                  </td>

                  {/* AI요약 */}
                  <td className="px-3 py-2.5 text-xs max-w-[200px]">
                    <span className="line-clamp-2 text-muted-foreground">
                      {appointment.purpose || "-"}
                    </span>
                  </td>

                  {/* 상태 */}
                  <td className="px-3 py-2.5 text-center">
                    <AppointmentStatusBadge status={appointment.status} />
                  </td>

                  {/* 액션 */}
                  <td
                    className="px-3 py-2.5 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ReservationQuickActions
                      appointment={appointment}
                      onDetailClick={() => onAppointmentClick(appointment)}
                      onPrioritySelect={onPrioritySelect}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2 border-t bg-muted/30 text-xs text-muted-foreground">
        총 {appointments.length}건
      </div>
    </Card>
  );
}
